import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../../components/Icon.js";
import { useEditor } from "../store.js";

/** Shared state for one tab of the panel: which section is open and what the owner typed in "Find a setting". */
interface PanelCtx { query: string; setQuery: (q: string) => void; openId: string | null; setOpenId: (id: string | null) => void; firstId: string | null; claimFirst: (id: string) => void; allOpen: boolean; advanced: boolean; setAdvanced: (v: boolean) => void; forced: string | null }
const Ctx = createContext<PanelCtx | null>(null);

export const PanelProvider: React.FC<{ resetKey: string; allOpen?: boolean; children: React.ReactNode }> = ({ resetKey, allOpen = false, children }) => {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [firstId, setFirstId] = useState<string | null>(null);
  const [advanced, setAdvancedState] = useState<boolean>(() => { try { return localStorage.getItem("studio.panelMode") === "advanced"; } catch { return false; } });
  const setAdvanced = (v: boolean) => { setAdvancedState(v); try { localStorage.setItem("studio.panelMode", v ? "advanced" : "simple"); } catch { /* ignore */ } };
  useEffect(() => { setOpenId(null); setFirstId(null); setQuery(""); }, [resetKey]);
  // the timeline or the preview pointed at one section: open it (even in Essentials mode) and forget the pointer
  const wanted = useEditor((s) => s.inspectorSection);
  const [forced, setForced] = useState<string | null>(null);
  useEffect(() => { if (wanted) { setOpenId(wanted); setForced(wanted); setQuery(""); useEditor.getState().clearInspectorSection(); } }, [wanted]);
  const value = useMemo<PanelCtx>(() => ({ query, setQuery, openId, setOpenId, firstId, claimFirst: (id) => setFirstId((f) => f ?? id), allOpen, advanced: advanced || allOpen, setAdvanced, forced }), [query, openId, firstId, allOpen, advanced, forced]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

const norm = (s: string) => s.toLowerCase();
const matches = (query: string, ...texts: (string | undefined)[]) => { const q = norm(query.trim()); return !q || texts.some((t) => t && norm(t).includes(q)); };

/** Essentials shows the handful of settings most clips need; All settings shows everything. Always on screen at the top of the column; remembered on this Mac. */
export const ModeSwitch: React.FC = () => {
  const ctx = useContext(Ctx);
  if (!ctx || ctx.allOpen) return null;
  return (
    <div className="mode" role="radiogroup" aria-label="Which settings to show">
      <button role="radio" aria-checked={!ctx.advanced} className={!ctx.advanced ? "on" : ""} onClick={() => ctx.setAdvanced(false)} title="The few settings most clips need">Essentials</button>
      <button role="radio" aria-checked={ctx.advanced} className={ctx.advanced ? "on" : ""} onClick={() => ctx.setAdvanced(true)} title="Every option in this tab">All settings</button>
    </div>
  );
};

/** A named group of sections (Opener, Video, Pictures…): a clear heading, its sections held together under it, and a line saying how many more settings "All settings" would show here. */
const GroupCtx = createContext<{ report: (id: string, hidden: boolean) => void; openHere: (id: string) => void } | null>(null);
const groupKey = (title: string) => `studio.group.${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
export const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const ctx = useContext(Ctx);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  // folded or open, as the owner last left it on this Mac; a search or a pointer from the timeline opens it
  const [folded, setFoldedState] = useState<boolean>(() => { try { return localStorage.getItem(groupKey(title)) === "folded"; } catch { return false; } });
  const setFolded = (v: boolean) => { setFoldedState(v); try { localStorage.setItem(groupKey(title), v ? "folded" : "open"); } catch { /* ignore */ } };
  const searching = !!ctx?.query.trim();
  const wantedInside = useRef<string | null>(null);
  const openHere = (id: string) => { wantedInside.current = id; };
  useEffect(() => { if (ctx?.forced && wantedInside.current === ctx.forced && folded) setFolded(false); }, [ctx?.forced]);
  const showBody = !folded || searching || !!ctx?.allOpen;
  const report = (id: string, h: boolean) => setHidden((prev) => { if (prev.has(id) === h) return prev; const next = new Set(prev); if (h) next.add(id); else next.delete(id); return next; });
  const value = useMemo(() => ({ report, openHere }), []);
  return (
    <GroupCtx.Provider value={value}>
      <section className={`sgroup${showBody ? "" : " folded"}`} aria-label={title}>
        <h3 className="sgroup-title"><button type="button" className="sgroup-fold" aria-expanded={showBody} onClick={() => setFolded(showBody)} title={showBody ? `Fold ${title} away` : `Open ${title}`}><span>{title}</span><Icon name={showBody ? "chev-down" : "chev-right"} size={15} color={showBody ? "var(--gold)" : "var(--muted)"} /></button></h3>
        <div className="sgroup-body" hidden={!showBody}>{children}</div>
        {hidden.size && ctx && !ctx.advanced ? <button className="sgroup-more" onClick={() => ctx.setAdvanced(true)}>{hidden.size} more setting{hidden.size === 1 ? "" : "s"} here · Show all</button> : null}
      </section>
    </GroupCtx.Provider>
  );
};

/** Content that only shows in Advanced mode (or while searching for it). */
export const Advanced: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ctx = useContext(Ctx);
  if (ctx && !ctx.advanced && !ctx.query.trim()) return null;
  return <>{children}</>;
};

/** The "Find a setting…" box at the top of a tab. */
export const PanelSearch: React.FC<{ placeholder?: string }> = ({ placeholder = "Find a setting…" }) => {
  const ctx = useContext(Ctx);
  if (!ctx) return null;
  return (
    <div className="row" style={{ gap: 8, margin: "0 0 2px", padding: "7px 10px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--panel2)", flexShrink: 0 }}>
      <Icon name="search" size={14} />
      <input value={ctx.query} onChange={(e) => ctx.setQuery(e.target.value)} placeholder={placeholder} aria-label="Find a setting" style={{ flex: 1, border: 0, background: "transparent", padding: 0, fontSize: 12 }} />
      {ctx.query ? <button onClick={() => ctx.setQuery("")} title="Clear" style={{ color: "var(--muted)" }}><Icon name="x" size={12} /></button> : null}
    </div>
  );
};

/** One concern of the clip: a title, a plain line saying what it is for, and its current value when closed.
 *  One section is open at a time; searching shows only the sections that mention the words typed. */
export const Section: React.FC<{ id: string; title: string; hint: string; summary?: string; badge?: string; keywords?: string; defaultOpen?: boolean; advanced?: boolean; children: React.ReactNode }> = ({ id, title, hint, summary, badge, keywords, defaultOpen, advanced, children }) => {
  const ctx = useContext(Ctx);
  const grp = useContext(GroupCtx);
  const [localOpen, setLocalOpen] = useState(!!defaultOpen);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => { if (ctx && defaultOpen) ctx.claimFirst(id); }, [ctx, defaultOpen, id]);
  const hit = ctx ? matches(ctx.query, title, hint, summary, badge, keywords) : true;
  const searching = !!ctx?.query.trim();
  const folded = !!advanced && !!ctx && !ctx.advanced && !searching && ctx.forced !== id;
  // the group counts the sections folded away in Essentials mode, so it can say how many "All settings" would add
  useEffect(() => { grp?.report(id, folded && hit); return () => grp?.report(id, false); }, [grp, id, folded, hit]);
  useEffect(() => { if (ctx?.forced === id) grp?.openHere(id); }, [ctx?.forced, id, grp]);
  // null: nothing chosen yet, so the default section shows; "": the owner closed the open one, so none shows
  const open = searching || ctx?.allOpen ? true : ctx ? (ctx.openId !== null ? ctx.openId === id : ctx.firstId === id || (!ctx.firstId && !!defaultOpen)) : localOpen;
  // opening brings the section to the top of the column so its controls are in view; closing keeps the folded row in view
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current === open) return;
    wasOpen.current = open;
    const el = root.current;
    if (!el || searching || ctx?.allOpen) return;
    const reduce = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: open ? "start" : "nearest", behavior: reduce ? "auto" : "smooth" });
  }, [open, searching, ctx?.allOpen]);
  if (!hit) return null;
  if (folded) return null;
  const toggle = () => { if (searching || ctx?.allOpen) return; if (ctx) ctx.setOpenId(open ? "" : id); else setLocalOpen(!open); };
  return (
    <div ref={root} className={`sec${open ? " open" : ""}`} data-section={id} style={{ border: `1px solid ${open ? "var(--line-strong)" : "var(--line)"}`, borderRadius: 14, background: "var(--panel)", overflow: "hidden", flexShrink: 0, scrollMarginTop: 56 }}>
      <button onClick={toggle} className="row" style={{ width: "100%", gap: 10, padding: "12px 14px", textAlign: "left", alignItems: "center" }} aria-expanded={open}>
        <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{title}</span>{badge ? <span style={{ fontSize: 10, fontWeight: 700, color: "var(--on-gold)", background: "var(--gold)", padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap" }}>{badge}</span> : null}</div>
          <span className="hint">{hint}</span>
        </div>
        {!open && summary ? <span style={{ fontSize: 11, color: "var(--text-2)", whiteSpace: "nowrap", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>{summary}</span> : null}
        <Icon name={open ? "chev-down" : "chev-right"} size={16} color={open ? "var(--gold)" : "var(--muted)"} />
      </button>
      {open ? <div className="col" style={{ gap: 14, padding: "12px 14px 14px", borderTop: "1px solid var(--line)" }}>
        {children}
        {!searching && !ctx?.allOpen ? <button onClick={toggle} className="row sec-close" aria-label={`Close ${title}`}><Icon name="chev-down" size={14} color="var(--gold)" />Close {title}</button> : null}
      </div> : null}
    </div>
  );
};

/** A setting: what it is called (left, with an optional plain line under it) and its control (right). `stack` puts a wide control underneath. */
export const Row: React.FC<{ label: string; hint?: string; stack?: boolean; children: React.ReactNode }> = ({ label, hint, stack, children }) => (
  <div className={stack ? "col" : "row"} style={stack ? { gap: 8 } : { justifyContent: "space-between", gap: 12, alignItems: "center" }}>
    <div className="col" style={{ gap: 2, minWidth: 0 }}><span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>{hint ? <span className="hint">{hint}</span> : null}</div>
    {children}
  </div>
);

/** Rarely-needed settings, folded away behind one line. */
export const More: React.FC<{ label?: string; children: React.ReactNode }> = ({ label = "More options", children }) => {
  const ctx = useContext(Ctx);
  const [open, setOpen] = useState(false);
  if (ctx && !ctx.advanced && !ctx.query.trim()) return null; // Simple mode: the rarely used rows stay out of the way entirely
  return (
    <div className="col" style={{ gap: 12 }}>
      <button onClick={() => setOpen(!open)} className="row" style={{ gap: 6, fontSize: 12, fontWeight: 600, color: "var(--gold)", alignSelf: "flex-start" }}><Icon name={open ? "chev-down" : "chev-right"} size={14} color="var(--gold)" />{label}</button>
      {open ? children : null}
    </div>
  );
};

/** A quiet explanatory line inside a section. */
export const Note: React.FC<{ children: React.ReactNode; tone?: "plain" | "good" | "warn" }> = ({ children, tone = "plain" }) => (
  <div style={{ padding: "8px 10px", borderRadius: 10, fontSize: 12, lineHeight: 1.45, background: tone === "good" ? "var(--green-dim)" : tone === "warn" ? "rgba(224,101,91,0.1)" : "var(--panel2)", border: `1px solid ${tone === "good" ? "rgba(34,177,76,0.5)" : tone === "warn" ? "#6b3d39" : "var(--line)"}`, color: tone === "plain" ? "var(--text-2)" : "var(--text)" }}>{children}</div>
);
